import axios from 'axios'
import isNil from 'lodash/isNil'
import apiClient from 'enrollment-platform/api/enrollment-platform-client/api-client'
import {
  parseContentDispositionFilename,
  saveBlobToDisk,
} from '../utils/blob-download'
import {
  BulkSendDeliveryRequestDto,
  BulkSyncSendGridResponseDto,
  BulkUpdateDeliveryStatusResponseDto,
  DeleteDeliveryResponseDto,
  DownloadInvoiceArchiveRequestDto,
  GetClientsResponseDto,
  GetDeliveriesRequestDto,
  GetDeliveryStatsRequestDto,
  InvoiceDeliveryEmailLogsResponseDto,
  InvoiceDeliveryGlobalSettingsDto,
  InvoiceDeliveryHistoryResponseDto,
  InvoiceDeliveryItemDto,
  InvoiceEmailPreviewDto,
  PreviewDraftEmailRequestDto,
  InvoiceDeliveryListResponseDto,
  InvoiceDeliveryStatsResponseDto,
  InvoiceTemplate,
  MatchDeliveryRequestDto,
  SendPreviewRequestDto,
  SendPreviewResponseDto,
  SendSelectedRequestDto,
  SetDeliveryArchivedRequestDto,
  UpdateInvoiceDeliveryDueDateRequestDto,
  UpdateInvoiceDeliveryOeSplitRequestDto,
  UpdateInvoiceDeliveryTemplateRequestDto,
  UpdateInvoiceDeliveryGlobalSettingsDto,
  UpdateInvoiceDeliveryMetadataRequestDto,
  UploadInvoiceFilesResponseDto,
  UploadResultItemDto,
} from './dto/invoice-delivery.dto'

const BASE_PATH = '/funding-invoice/delivery'

export const uploadInvoiceFiles = async (
  files: File[],
  billingPeriodStart: string
): Promise<UploadInvoiceFilesResponseDto> => {
  const formData = new FormData()
  files.forEach((file) => formData.append('files', file))
  formData.append('billingPeriodStart', billingPeriodStart)

  const response = await apiClient.post<UploadInvoiceFilesResponseDto>(
    `${BASE_PATH}/upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return response.data
}

export const UPLOAD_TIMEOUT_MS = 45_000

/** One file's upload. Null leaves a value to the sheet or the billing profile. */
export interface UploadInvoiceFileOptions {
  billingPeriodStart: string
  invoiceTemplate: InvoiceTemplate
  dueDate: string | null
  totalDue: number | null
  balanceDue: number | null
  oeTotalDue: number | null
  /** Company chosen in the review when the sheet name matched none. */
  clientId: string | null
}

export const uploadInvoiceFile = async (
  file: File,
  options: UploadInvoiceFileOptions,
  timeoutMs: number = UPLOAD_TIMEOUT_MS
): Promise<UploadResultItemDto> => {
  const source = axios.CancelToken.source()
  const timeoutId = setTimeout(() => source.cancel('Upload timeout'), timeoutMs)

  const formData = new FormData()
  formData.append('files', file)
  for (const [key, value] of Object.entries(options)) {
    if (!isNil(value)) formData.append(key, String(value))
  }

  try {
    const response = await apiClient.post<UploadInvoiceFilesResponseDto>(
      `${BASE_PATH}/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        cancelToken: source.token,
      }
    )
    const result = response.data.results[0]
    if (!result) throw new Error('Server returned no result for this file')
    return result
  } finally {
    clearTimeout(timeoutId)
  }
}

export const getDeliveries = async (
  params: GetDeliveriesRequestDto
): Promise<InvoiceDeliveryListResponseDto> => {
  const response = await apiClient.get<InvoiceDeliveryListResponseDto>(
    BASE_PATH,
    { params }
  )
  return response.data
}

export const getDeliveryStats = async (
  params: GetDeliveryStatsRequestDto
): Promise<InvoiceDeliveryStatsResponseDto> => {
  const response = await apiClient.get<InvoiceDeliveryStatsResponseDto>(
    `${BASE_PATH}/stats`,
    { params }
  )
  return response.data
}

export const deleteDelivery = async (
  id: string
): Promise<DeleteDeliveryResponseDto> => {
  const response = await apiClient.delete(`${BASE_PATH}/${id}`)
  return response.data as DeleteDeliveryResponseDto
}

export const sendDelivery = async (
  id: string
): Promise<InvoiceDeliveryItemDto> => {
  const response = await apiClient.post<InvoiceDeliveryItemDto>(
    `${BASE_PATH}/${id}/send`
  )
  return response.data
}

export const bulkSendDeliveries = async (
  body: BulkSendDeliveryRequestDto
): Promise<BulkUpdateDeliveryStatusResponseDto> => {
  const response = await apiClient.post<BulkUpdateDeliveryStatusResponseDto>(
    `${BASE_PATH}/bulk-send`,
    body
  )
  return response.data
}

/**
 * Who a send over this selection would contact, per delivery and per
 * recipient. Read-only despite being a POST — the id list is unbounded, so it
 * does not fit a query string.
 */
export const previewSend = async (
  body: SendPreviewRequestDto
): Promise<SendPreviewResponseDto> => {
  const response = await apiClient.post<SendPreviewResponseDto>(
    `${BASE_PATH}/send-preview`,
    body
  )
  return response.data
}

/**
 * Sends each delivery to exactly the addresses supplied — including any that
 * already received the invoice. The only path that can deliberately re-send a
 * delivered invoice; call it with what the operator confirmed in the modal.
 */
export const sendSelected = async (
  body: SendSelectedRequestDto
): Promise<BulkUpdateDeliveryStatusResponseDto> => {
  const response = await apiClient.post<BulkUpdateDeliveryStatusResponseDto>(
    `${BASE_PATH}/send-selected`,
    body
  )
  return response.data
}

/** With a period, each client also carries its billing profile's terms for it. */
export const getDeliveryClients = async (
  billingPeriodStart?: string
): Promise<GetClientsResponseDto> => {
  const response = await apiClient.get<GetClientsResponseDto>(
    `${BASE_PATH}/clients`,
    { params: isNil(billingPeriodStart) ? {} : { billingPeriodStart } }
  )
  return response.data
}

export const getDeliveryEmailPreview = async (
  id: string
): Promise<InvoiceEmailPreviewDto> => {
  const response = await apiClient.get<InvoiceEmailPreviewDto>(
    `${BASE_PATH}/${id}/email-preview`
  )
  return response.data
}

export const previewDraftEmail = async (
  body: PreviewDraftEmailRequestDto
): Promise<InvoiceEmailPreviewDto> => {
  const response = await apiClient.post<InvoiceEmailPreviewDto>(
    `${BASE_PATH}/email-preview`,
    body
  )
  return response.data
}

export const matchDelivery = async (
  id: string,
  body: MatchDeliveryRequestDto
): Promise<InvoiceDeliveryItemDto> => {
  const response = await apiClient.patch<InvoiceDeliveryItemDto>(
    `${BASE_PATH}/${id}/match`,
    body
  )
  return response.data
}

export const updateInvoiceDeliveryMetadata = async (
  id: string,
  body: UpdateInvoiceDeliveryMetadataRequestDto
): Promise<InvoiceDeliveryItemDto> => {
  const response = await apiClient.patch<InvoiceDeliveryItemDto>(
    `${BASE_PATH}/${id}/metadata`,
    body
  )
  return response.data
}

export const updateInvoiceDeliveryDueDate = async (
  id: string,
  body: UpdateInvoiceDeliveryDueDateRequestDto
): Promise<InvoiceDeliveryItemDto> => {
  const response = await apiClient.patch<InvoiceDeliveryItemDto>(
    `${BASE_PATH}/${id}/due-date`,
    body
  )
  return response.data
}

export const updateInvoiceDeliveryOeSplit = async (
  id: string,
  body: UpdateInvoiceDeliveryOeSplitRequestDto
): Promise<InvoiceDeliveryItemDto> => {
  const response = await apiClient.patch<InvoiceDeliveryItemDto>(
    `${BASE_PATH}/${id}/oe-split`,
    body
  )
  return response.data
}

export const updateInvoiceDeliveryTemplate = async (
  id: string,
  body: UpdateInvoiceDeliveryTemplateRequestDto
): Promise<InvoiceDeliveryItemDto> => {
  const response = await apiClient.patch<InvoiceDeliveryItemDto>(
    `${BASE_PATH}/${id}/template`,
    body
  )
  return response.data
}

export const getDeliveryHistory = async (
  id: string,
  /** Mirrors the list's archive scope. Omit to return the whole thread. */
  isArchived?: boolean
): Promise<InvoiceDeliveryHistoryResponseDto> => {
  const response = await apiClient.get<InvoiceDeliveryHistoryResponseDto>(
    `${BASE_PATH}/${id}/history`,
    { params: isNil(isArchived) ? undefined : { isArchived } }
  )
  return response.data
}

export const setDeliveryArchived = async (
  id: string,
  body: SetDeliveryArchivedRequestDto
): Promise<InvoiceDeliveryItemDto> => {
  const response = await apiClient.patch<InvoiceDeliveryItemDto>(
    `${BASE_PATH}/${id}/archive`,
    body
  )
  return response.data
}

export const getDeliveryEmailLogs = async (
  id: string
): Promise<InvoiceDeliveryEmailLogsResponseDto> => {
  const response = await apiClient.get<InvoiceDeliveryEmailLogsResponseDto>(
    `${BASE_PATH}/${id}/email-logs`
  )
  return response.data
}

/*
 * Fetches the rendered email HTML through the authenticated API client and
 * returns a Blob URL. We can't just point window.open at the endpoint
 * directly — the auth interceptor lives on apiClient (Authorization header),
 * and a new tab wouldn't carry it. Caller is responsible for revoking the
 * URL when the tab is no longer needed (we revoke on a generous timer).
 */
export const getDeliveryEmailLogPreviewBlobUrl = async (
  deliveryId: string,
  logId: string
): Promise<string> => {
  const response = await apiClient.get<string>(
    `${BASE_PATH}/${deliveryId}/email-logs/${logId}/preview`,
    { responseType: 'text' }
  )
  const blob = new Blob([response.data], { type: 'text/html;charset=utf-8' })
  return URL.createObjectURL(blob)
}

/*
 * Zipping a whole billing period streams ~100 files out of S3, so the default
 * client timeout is too tight. Generous but still bounded, so a wedged request
 * eventually surfaces as an error instead of a spinner that never resolves.
 */
export const ARCHIVE_DOWNLOAD_TIMEOUT_MS = 300_000

/**
 * Bytes received so far for an in-flight archive download.
 *
 * There is no `percent`, deliberately. The endpoint streams the ZIP as it is
 * built, so the response carries no `Content-Length` and the browser cannot
 * know the total — `ProgressEvent.lengthComputable` is false. Showing a
 * percentage would mean inventing a denominator.
 */
export interface ArchiveDownloadProgress {
  receivedBytes: number
}

/**
 * Downloads a ZIP of invoice files and saves it to disk.
 *
 * POST rather than GET because the id list can reach MAX_ARCHIVE_FILE_COUNT
 * (500) entries: ~215 UUIDs already overflow NGINX's default 8KB request-line
 * limit, so the proxy would reject requests the endpoint's own cap allows.
 * The scope therefore travels as a JSON body — `ids` as a real array, not
 * comma-joined.
 *
 * Goes through `apiClient` rather than pointing the browser at the URL: the
 * Authorization header lives on the client's interceptor, so a plain
 * `window.open` would hit the endpoint unauthenticated (same reasoning as
 * `getDeliveryEmailLogPreviewBlobUrl` above). A POST could not be driven that
 * way in any case — hence reading the response as a Blob and saving it via a
 * temporary object URL.
 */
export const downloadInvoiceArchive = async (
  params: DownloadInvoiceArchiveRequestDto,
  onProgress?: (progress: ArchiveDownloadProgress) => void
): Promise<{ fileName: string }> => {
  const response = await apiClient.post<Blob>(
    `${BASE_PATH}/bulk-download`,
    params,
    {
      responseType: 'blob',
      timeout: ARCHIVE_DOWNLOAD_TIMEOUT_MS,
      // Axios 0.17 forwards the raw XHR ProgressEvent.
      onDownloadProgress: (event: ProgressEvent) => {
        onProgress?.({ receivedBytes: event.loaded })
      },
    }
  )
  const fileName =
    parseContentDispositionFilename(
      response.headers['content-disposition'] as string | undefined
    ) ?? 'funding-invoices.zip'
  saveBlobToDisk(response.data, fileName)
  return { fileName }
}

export const bulkSyncSendGrid = async (
  billingPeriodStart: string
): Promise<BulkSyncSendGridResponseDto> => {
  const response = await apiClient.post<BulkSyncSendGridResponseDto>(
    `${BASE_PATH}/bulk-sync-sendgrid`,
    { billingPeriodStart }
  )
  return response.data
}

export const getInvoiceDeliveryGlobalSettings =
  async (): Promise<InvoiceDeliveryGlobalSettingsDto> => {
    const response = await apiClient.get<InvoiceDeliveryGlobalSettingsDto>(
      `${BASE_PATH}/global-settings`
    )
    return response.data
  }

export const updateInvoiceDeliveryGlobalSettings = async (
  body: UpdateInvoiceDeliveryGlobalSettingsDto
): Promise<InvoiceDeliveryGlobalSettingsDto> => {
  const response = await apiClient.patch<InvoiceDeliveryGlobalSettingsDto>(
    `${BASE_PATH}/global-settings`,
    body
  )
  return response.data
}
